import { assign, actions, createMachine } from 'xstate'
import {
  getMerchantLmnInfo,
  sendOneTimePassword,
  verifyOneTimePassword,
  submitLmnForm,
} from 'src/services/lmnService'
import {
  fetchServiceReturn,
  getMerchantLmnInfoReturn,
} from 'src/helpers/serviceHelpers'
import {
  ILmnContext,
} from 'src/helpers/lmnHelpers'
// import { LMN_PROVIDER } from 'src/constants/global'
const { choose } = actions

const LMN_INTAKE_FORM_STEP = 3
const LMN_MEDICAL_PROFILE_STEP = 5

type LmnEvent =
  | {
    type: 'NEXT_LMN_STEP'
  }
  | {
    type: 'SET_CUSTOMER_INFO'
    data: {
      isSmsTelehealthConsentChecked?: boolean
      isTelehealthAndPrivacyConsentChecked?: boolean
      firstName?: string
      lastName?: string
      email?: string
      phone?: string
      dateOfBirth?: string
      sex?: string
      addressLine1?: string
      addressLine2?: string
      city?: string
      state?: string
      postalCode?: string
    }
  }
  | {
    type: 'SET_MEDICAL_PROFILE_INFO'
    data: {
      selfReportedMeds?: string
      allergies?: string
      medicalConditions?: string
      Q1?: string
      A1?: string
      Q2?: string
      A2?: string
      Q3?: string
      A3?: string
      hasMedicalHistory?: string
      hasAllergiesOrReactions?: string
      hasSelfReportedMeds?: string
      hasOtherQuestions?: string
    }
  }
  | {
    type: 'MEDICAL_PROFILE_COMPLETE'
    data: {
      selfReportedMeds: string
      allergies: string
      medicalConditions: string
      Q1: string
      A1: string
      Q2: string
      A2: string
      Q3: string
      A3: string
    }
  }
  | {
    type: 'ON_LOAD'
    data: {
      merchantUuid: string
      onCancel: (() => void) | undefined
    }
  }
  | {
    type: 'SEND_NOT_QUALIFIED'
    data: {
      lmnNotQualified: boolean
    }
  }
  | { type: 'ON_CANCEL' }
  | { type: 'ON_BACK' }
  | { type: 'RESET_CONTEXT' }
  | {
    type: 'SET_DIAGNOSIS'
    data: {
      diagnosis: string
    }
  }
  | {
    type: 'SEND_OTP'
    data: {
      firstName: string
      lastName: string
      addressLine1: string
      addressLine2: string
      city: string
    }
  }
  | {
    type: 'VERIFY_OTP'
    data: {
      code: string
    }
  }
  | {
    type: 'RESEND_OTP'
    data: {
      phone: string
    }
  }
  | {
    type: 'SET_CUSTOMER_IMAGE'
    data: {
      customerImage: {
        data: string
        mime: string
      } | null
    }
  }
  | { type: 'SUBMIT_LMN_FORM' }

interface InitialLmnContext extends Omit<ILmnContext, 'onCancel'> { }

const initialContext: InitialLmnContext = {
  customerInfo: {
    isSmsTelehealthConsentChecked: false,
    isTelehealthAndPrivacyConsentChecked: false,
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phone: '',
    email: '',
    confirmEmail: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    sex: '',
    code: '',
    phoneVerificationTimestamp: '',
    selfReportedMeds: '',
    allergies: '',
    medicalConditions: '',
    diagnosis: '',
    hasMedicalHistory: 'No',
    hasAllergiesOrReactions: 'No',
    hasSelfReportedMeds: 'No',
    hasOtherQuestions: 'No',
    Q1: '',
    A1: '',
    Q2: 'Do you have any questions for the healthcare provider?',
    A2: '',
    Q3: 'What medications, therapies, or other treatments have you tried for this condition? If none, please enter "none"',
    A3: '',
  },
  codeError: false,
  lmnConditions: [],
  lmnContraindications: [],
  lmnId: '',
  lmnProvider: '',
  lmnStatus: '', // pending, approved, none, null
  lmnStep: 0,
  lmnNotQualified: false,
  merchant: '',
  merchantUuid: '',
  merchantLogoUrl: '',
  merchantName: '',
  merchantWebsite: '',
  customerImage: null,
  loading: false,
  smallLoading: false,
  merchantProcessor: '',
}

/* eslint-disable @typescript-eslint/explicit-function-return-type, max-len */
/** @xstate-layout N4IgpgJg5mDOIC5QAcCGBPAtmAdgF1gBEBLVAGwHsoBZVAYwAticwA6O1HOsM14iMmADEAYQCCAOREBRADIB9APIAlQtOUBtAAwBdRCgqxieYhRz6QAD0QAWAJwB2VgEYATPYCsADgBsDu342AMwANCDoiM5erqxeDvbRcXYeHq5BPgC+GWFoWLgEJORUtIzMbBxcPOyc3GRkzFCKAE4QYE1CEGZszABuFADW5TU89TiNLW3aekggyIbGpuYz1gg2DjF2m5sOWj7erg7pYREIrl4eLu52a85Bm+cOHlk5GNj4RKSUNPRMLNWVvAqtVG41a7TaTQoTVYyDIqDwADMoZh-sCGs0wVMLHMjCYzBYVmsNltHLt9ocfMdEGkLiTfD4gtFHF4vM9Zq98h8it9Sn8cMjyHwBMJxFI5EpVOosTMcQt8ctEF4bBcPD5XB5-OsvMl1VSEFFXDFDdEfMEztqDmzcm8Cp9ij8yqx+U1MIL+IIhABlaQAFXkcgAkgBxAMAIVk0nk4lU8gDEgAYoppQZcYsCYqtHZWHYgo9TcrnBrUnrnOsfKwDvY-B4tDWgh4bFaOe9Cl8Sr82M7Xbx3cJvX7AyHw5GAApiACa1GkEgHEkIcaDydm8zxS1AK2cUSzri0QWcjncPi8WjSJZ3XlYHjuWgcR60a2STbyLbtPI7ToFPeFXt9-tkwbDCN5DHSdp1necJEXZxphTOU1ysRAy1iDxnB8as9yiK8SwZLRWCCVxUJ2I9NwcUinxtLk2wdPlPyFD1+1jCRB0AyNo0IRjEyXWVV3TBBjxiPd1QfOJtRsSlwkiK8bDwo9PELGsDlccjOVbe1eU7Wjex-P042Y4dgInKcZ39OcFy4lc0wVfUtwrXd93WMT+NCCT9SCe9WHsAi913fDGUbbJ2WfW1uXbR0uzdb8GN0-8hyAkCjPAszoOxCz5XXRD9wrIIbBsZxcp3R47GcEtggvXKGxsTNXD2Lw7mUl8QuojSXQij1FAkeRZEUMRCHM1M0oQ6ztVsvcD0ck9nJONJdlYTNnHvbxKviBx6uCqj1I-Fqv3ogBVUNqADP0VDUTRdBS-r4I3Gyd1Ghy71PFy8q0XCcpQ85qtQrxS1Wyi1PfcLeFgABXAAjTBjBMMYMTaDouj4HA+kGVhgbBiH0QmJo+rg3iCL8S8HGiXZ4j3RkHGw48ssLZJc1SE8VoC60VNfUKaK25HQfBvBIdBGGIShGE4URZF2dRrn0cxM6ZVSy7EDc9JswIuxD0SGwlWw1Xsx2O4Ukq+aUh+1S3zC2iUc57nofBJpIWhWF4SRF0RbN8XJmSqWLpxtwnBu1xrhSbKiz1I8Lx2H27luMSc2cA3maazbu0dtGoYxoQ+ZtwX7ZRU3E55zHXBg5d3asnc1WQ64Q5rTdXBLLRCxcVUonsZa5ejxqNoBhOxaTsEU6t-nbaFh2s87nONCCfPuMs9LTlrGIbENBa8oCaq9WqnxcKiRkUgImv1Rb9b-pNjns4tnvrYFu3haH82MY0Gxx+lnHjQVncAkLZUTw8Esr3LUilTVGtbzXCCHvP6xs2Y8GIFAYgINBAiFQC0eMqBiCCAgEIH0iggxBiAl1HqC55CSADNQMQPoAztSxjxKy2VoisB8DqNeQRcy+F8CvO45YrxaHOKWdwJ4AggKNqzeOECoEwLAHAhBSCUGiEkDIBQx0pSS1ghQqevkLjxE2CRHY2pP6PU2BcXwOUfB5UIl9PhLNmqCPqMI2B8CICIOQZAbSjE9JATYhxJMCiC7Y0oYvCs9g7AcJzLeBhK8UI-xvNVeIax7y8IZs2NaoCBFuhYJY6B1jxH2NQegzB2DuoQSDPgiQhDiGkIkOQyeg1fB4UeLWNC6xri7m0ScQxWZ6yr1VB4HMwR-IvCCr9fh5iklCNSaImxdjJGihkRKE6ZSBorEZEEFwmZ8r3jVNWPUSoFn+LXsERkC0GGmNju3MoKSRFiNsRIhxUUmIxRYlGMQMY4ycQ8RPWZiBHCxHrI4NCDIHClmuHqVICy7ivzym5fxNcDlt00skyBwyzljMub+aKAF9LxTAiZPJMyZYIHmYs64bgVkRMabYX52Ytiky0f4OwkKD5s2ObC05oyLmoM9HtA6R1JSnXvoXZRtU8XLLEkSgFeMdSbDclECaSlYm9MNmYuOgohmMvSSg6gxBLCQFDKgWAgw8BoIwVgyMOC8kFKKSQshzyH5WXWDQ00VVUI7nVMkbCKFLyq3SBwsS3yo7SoorKw5tFFVpPORk1V6qICau1WAXVEzxRyK5edLxvKFnzXxTuQVazHq1WcLNAmtxDQNlSDSsBFiGVBoRRAUNGqtU6q9Gyw6Uz5HcsTYNXFKaBWrMeOs64HldilkJkrG8RbEm8EDSM5VkBK3hurVGxxyLYqsXuexR57im1KJbXyttBL02dseocC4bhPB+DOOka4Q6Bk9hhVYsdwaVVqqrZG3VWSDWdVyXgghRCzWlItTywa-gXBxFSLVdIUTSwr1Kr435qoKSq3pj031McoV0svXCplIa71TofVIsUsjOVYt4q2pZW6O3Ev1CybNGovrKgJikNC3TArwdbrS+O9Kr3wuZZOiNNarnOIXQ8hMK6E1rrmRuwjabiMhNuB5Q4YrfC1jPfK3gMAWBNGIHQMZQMmjCCfTk3BkETUfpKXhqypZGQuDEs9c4J4DRfwIi4ZI+4HXrDkz6pmjHi2CiU20VT6nNNYcmXGozU9CzPQ8kHaI6QNS1mwpJ1U9h5q1KeqyFzDV97ucU7gLzamJEab7HWjl0zv3NpWDuVIHl54FirMvR6aosyHELF9JU5U1Tyfbpp1AEB0DxihMoKNTR0B+djbhwrQnJIhcciyNIewdgkaiI8FwoL6knkqjlFrtE2sda600HreA+uzuuSilxi63GBcGiZlpwQcz3BrqwsDDC8KOGVL4A4ex7yrbZutzr3Xev9e4zc1Fhl0XTkxcN8pG5fnJriN8vcwQrxVxcmkXMHkcrLeBQVN78cPube27t1l+160BZB686epW55ppQpV8SU1coXiPH5Qi-iCbAOS-E-pCnWCUHa87JoAYcAZ3hIsWGfxegDDYBziAXOed89XCd4rM8ytk8XqslevgsxiRQtle8vhVSZGZ30uV7cxcS95wKVcp8+7p2FobrubRJcm8WDLyIc9pJfQOG4NeNSGzK9royWhHTlTeHOOjwUVuc625aquctQh2ovp6g7oa247JjXupNakT3Ygq-pw5bKQfeAh5HJCbgsBYBQjD66U3nQhcIxF+zignOxj54oIX4v3Pjfh-t4T7FhpTTZlh6heS7g1bw9QtJTcOVfBcNCU8XXfrEPxzzwXuAzfS-87MGbtOF8Hbz8b4vkvrey-t9XaD6kq8KwBB1G-Q9K9NzlkMcqc0GFvBZACvyVo8AZRxL101QTR+EAAFp3B6j-50aMwpYJJDAAjf5E4AEuQMgeQkgGi1SHCB7T4IbvhAhVC9iQHYp+Cq65g3BpANY2AsJOB+LJDnBdJXgajyboGAjDB1Bc5YG8TrBfy4R0gRaBBkQoFubDqMFFzSS0Z2pqgfw6h6h+RwGbARyGLFzepwauapbDp0RgC8FTwWgVizbrDzJEx2AlhxBOBaiFh7BqhpC7xcHyHnodzXxgjKGDTVTKjZi3C7gnh7BuDFTVa2YvaZhKz+BrAMg56sCjpsYZLWFzJzwXi7DuDuD4T1IkZXAkG1T+IdKpBrDZ6mFgFs4sYobjoQDBGRBuQKz7huCpAKQ5Rw4nBOZ4S1hLxuo7ibh+EBGoa3phqcZRo5EICkQliPAxAVzfy5hnCoQ66yGgGs5HLIZKo3oTrobNF4CtHtHw5XgJ57Kqi7gGJ+GeYqZZbII5atESoxCaKw4ah5SVjYSGhmbRCCo0aqxSqDEs765rZgDtafZbbfbbH+CzxHiXb1g+5zwhLZR4Q1goRnBLGBJ+Eh4WzL5rovLYqlgnisC5r+4ahuTETK54y0KlgOSGg7i7ggm17i7W4t5S6LDlozGZRpA5RGIFQdKuFTT+LSTVEYTvy37Yl15QAN5N674EkDSQk4y2r5HJDeF5SqxX4pB4SVQBBHjHpuRSpZBAA */
const lmnMachine = () => createMachine(
  {
    id: 'lmnMachine',
    predictableActionArguments: true,
    // Type definitions of the machine are handled in the schema
    schema: {
      context: {} as ILmnContext,
      events: {} as LmnEvent,
      services: {} as {
        // The data that gets returned from the service
        getMerchantLmnInfo: {
          data: getMerchantLmnInfoReturn
        }
        sendOneTimePassword: {
          data: fetchServiceReturn
        }
        verifyOneTimePassword: {
          data: fetchServiceReturn
        }
        submitLmnForm: {
          data: fetchServiceReturn
        }
      },
    },
    tsTypes: {} as import('./lmnMachine.typegen').Typegen0,
    context: {
      onCancel: undefined,
      ...initialContext,
    },
    type: 'parallel',
    states: {
      cancel: {
        initial: 'idle',
        states: {
          idle: {
            on: {
              ON_CANCEL: {
                target: 'idle',
                actions: ['callCancel', 'resetContext'],
              },
            },
          },
        },
      },
      normal: {
        initial: 'idle',
        states: {
          idle: {
            on: {
              ON_LOAD: {
                target: 'loadingMerchantInformation',
                actions: ['setMerchantUuid', 'setOnCancel'],
              },
              ON_BACK: {
                target: 'idle',
                actions: choose([
                  {
                    cond: 'hasVerificationCodeAndMedicalProfileStep',
                    actions: 'setLmnStepToIntakeFormStep',
                  },
                  { actions: 'decreaseLmnStep' },
                ]),
              },
              NEXT_LMN_STEP: {
                target: 'idle',
                actions: 'increaseLmnStep',
              },
              SET_CUSTOMER_INFO: {
                target: 'idle',
                actions: 'setCustomerInfo',
              },
              SET_MEDICAL_PROFILE_INFO: {
                target: 'idle',
                actions: 'setCustomerInfo',
              },
              MEDICAL_PROFILE_COMPLETE: {
                target: 'idle',
                actions: ['setCustomerInfo', 'increaseLmnStep'],
              },
              SET_DIAGNOSIS: {
                target: 'idle',
                actions: ['setCustomerInfo', 'increaseLmnStep'],
              },
              SEND_OTP: [{
                target: 'idle',
                cond: 'hasVerificationCode',
                actions: ['setCustomerInfo', 'setLmnStepToMedicalProfileStep'],
              }, {
                target: 'sendingOneTimePassword',
                actions: 'setCustomerInfo',
              }],
              VERIFY_OTP: {
                target: 'verifyingOneTimePassword',
                actions: ['setCustomerInfo'],
              },
              RESEND_OTP: {
                target: 'resendingOneTimePassword',
                actions: ['setCustomerInfo'],
              },
              SET_CUSTOMER_IMAGE: {
                target: 'idle',
                actions: ['setCustomerImage'],
              },
              SUBMIT_LMN_FORM: {
                target: 'submittingLmnForm',
              },
              SEND_NOT_QUALIFIED: {
                target: 'idle',
                actions: 'setLmnNotQualified',
              },
            },
          },
          loadingMerchantInformation: {
            entry: 'enableLoading',
            exit: 'disableLoading',
            invoke: {
              id: 'loadingMerchantInformation',
              src: 'getMerchantLmnInfo',
              onDone: {
                target: 'idle',
                actions: ['setMerchantInformation'],
              },
              onError: {
                target: 'idle',
                // TODO: do something here
              },
            },
          },
          sendingOneTimePassword: {
            entry: 'enableLoading',
            exit: 'disableLoading',
            invoke: {
              id: 'sendingOneTimePassword',
              src: 'sendOneTimePassword',
              onDone: {
                target: 'idle',
                actions: ['increaseLmnStep'],
              },
              onError: {
                target: 'idle',
                actions: ['disableLoading'],
              },
            },
          },
          resendingOneTimePassword: {
            entry: 'enableSmallLoading',
            exit: 'disableSmallLoading',
            invoke: {
              id: 'resendingOneTimePassword',
              src: 'sendOneTimePassword',
              onDone: {
                target: 'idle',
                actions: 'clearOtpCodeError',
              },
              onError: {
                target: 'idle',
                actions: ['disableSmallLoading'],
              },
            },
          },
          verifyingOneTimePassword: {
            entry: 'enableLoading',
            exit: 'disableLoading',
            invoke: {
              id: 'verifyingOneTimePassword',
              src: 'verifyOneTimePassword',
              onDone: {
                target: 'idle',
                actions: ['setVerifyOtpInfo', 'increaseLmnStep', 'clearOtpCodeError'],
              },
              onError: {
                target: 'idle',
                actions: ['disableLoading', 'clearOtp', 'setOtpCodeError'],
              },
            },
          },
          submittingLmnForm: {
            entry: 'enableLoading',
            exit: 'disableLoading',
            invoke: {
              id: 'submittingLmnForm',
              src: 'submitLmnForm',
              onDone: {
                target: 'idle',
                actions: ['increaseLmnStep'],
              },
              onError: {
                target: 'idle',
                actions: ['disableLoading'],
              },
            },
          },
        },
      },
    },
  },
  {
    actions: {
      resetContext: assign(initialContext),
      setOnCancel: assign({
        onCancel: (_context, { data }) => data.onCancel,
      }),
      callCancel: ({ onCancel }) => {
        if (typeof onCancel === 'function') {
          onCancel()
        }
      },
      setLmnStepToIntakeFormStep: assign({
        lmnStep: LMN_INTAKE_FORM_STEP,
      }),
      setLmnStepToMedicalProfileStep: assign({
        lmnStep: LMN_MEDICAL_PROFILE_STEP,
      }),
      increaseLmnStep: assign({
        lmnStep: ({ lmnStep }) => lmnStep + 1,
      }),
      decreaseLmnStep: assign({
        lmnStep: ({ lmnStep }) => lmnStep - 1,
      }),
      setMerchantUuid: assign({
        merchantUuid: (_context, { data }) => data.merchantUuid,
      }),
      setLmnNotQualified: assign({
        lmnNotQualified: (_context, { data }) => data.lmnNotQualified,
      }),
      setMerchantInformation: assign({
        lmnConditions: (_context, { data }) => (data as any).response?.lmn_conditions,
        lmnContraindications: (_context, { data }) => (data as any).response?.lmn_contraindications,
        lmnProvider: (_context, { data }) => (data as any).response?.lmn_provider,
        merchantUuid: (_context, { data }) => (data as any).response?.merchant_uuid,
        merchantLogoUrl: (_context, { data }) => (data as any).response?.merchant_logo,
        merchantName: (_context, { data }) => (data as any).response?.merchant_name,
        merchantWebsite: (_context, { data }) => (data as any).response?.merchant_website,
        merchantProcessor: (_context, { data }) => (data as any).response?.processor,
      }),
      setCustomerInfo: assign({
        customerInfo: ({ customerInfo }, { data }) => ({
          ...customerInfo,
          ...data,
        }),
      }),
      clearOtp: assign({
        customerInfo: ({ customerInfo }) => ({
          ...customerInfo,
          code: '',
        }),
      }),
      setOtpCodeError: assign({
        codeError: true,
      }),
      clearOtpCodeError: assign({
        codeError: false,
      }),
      setCustomerImage: assign({
        customerImage: (_context, { data }) => data.customerImage,
      }),
      setVerifyOtpInfo: assign({
        customerInfo: ({ customerInfo }, { data }) => ({
          ...customerInfo,
          phoneVerificationTimestamp: (data as any).response?.phoneVerificationTimestamp || '',
        }),
      }),
      enableLoading: assign({
        loading: true,
      }),
      disableLoading: assign({
        loading: false,
      }),
      enableSmallLoading: assign({
        smallLoading: true,
      }),
      disableSmallLoading: assign({
        smallLoading: false,
      }),
    },
    services: {
      getMerchantLmnInfo: async ({ merchantUuid }) => {
        const { error, response } = await getMerchantLmnInfo(merchantUuid)
        // NOTE: need to throw the error here, otherwise it will act
        // as if the promise succeeded
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        if (error) throw error

        return { error, response }
      },
      sendOneTimePassword: async ({ customerInfo }) => {
        const { error, response } = await sendOneTimePassword(
          customerInfo.phone,
        )
        // NOTE: need to throw the error here, otherwise it will act
        // as if the promise succeeded
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        if (error) throw error

        return { error, response }
      },
      verifyOneTimePassword: async ({ customerInfo }) => {
        const { error, response } = await verifyOneTimePassword(
          customerInfo.phone,
          customerInfo.code,
        )
        // NOTE: need to throw the error here, otherwise it will act
        // as if the promise succeeded
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        if (error) throw error

        return { error, response }
      },
      submitLmnForm: async ({ customerInfo, customerImage, merchantUuid }) => {
        const { error, response } = await submitLmnForm(
          customerInfo,
          customerImage,
          merchantUuid,
        )
        // NOTE: need to throw the error here, otherwise it will act
        // as if the promise succeeded
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        if (error) throw error

        return { error, response }
      },
    },
    guards: {
      hasVerificationCode: ({ customerInfo }) => customerInfo.code !== '',
      hasVerificationCodeAndMedicalProfileStep: ({ customerInfo, lmnStep }) => (customerInfo.code !== '') && lmnStep === LMN_MEDICAL_PROFILE_STEP,
    },
  },
)

export default lmnMachine
